/**
 * GMB Configuration Settings
 * Central configuration for Google My Business integration
 */

// Environment detection
const isDevelopment = import.meta.env.DEV
const isProduction = import.meta.env.PROD

/**
 * GMB Worker API Configuration
 */
export const GMB_WORKER_CONFIG = {
  baseUrl: isProduction
    ? 'https://fixmybrick-gmb-worker.floral-breeze-0a38.workers.dev'
    : 'https://fixmybrick-gmb-worker.floral-breeze-0a38.workers.dev', // Same URL for now
  
  endpoints: {
    // Account endpoints
    accounts: '/api/accounts',
    accountDetails: (accountId: string) => `/api/accounts/${accountId}`,
    
    // Location endpoints
    locations: '/api/locations',
    locationDetails: (locationId: string) => `/api/locations/${locationId}`,
    locationsByAccount: (accountId: string) => `/api/accounts/${accountId}/locations`,
    
    // Review endpoints
    reviews: (locationId: string) => `/api/locations/${locationId}/reviews`,
    reviewReply: (locationId: string, reviewId: string) => `/api/locations/${locationId}/reviews/${reviewId}/reply`,
    
    // Post endpoints
    posts: (locationId: string) => `/api/locations/${locationId}/posts`,
    postDetails: (locationId: string, postId: string) => `/api/locations/${locationId}/posts/${postId}`,
    
    // Question endpoints
    questions: (locationId: string) => `/api/locations/${locationId}/questions`,
    questionAnswer: (locationId: string, questionId: string) => `/api/locations/${locationId}/questions/${questionId}/answer`,
    
    // Sync endpoints
    syncAll: '/api/sync/all',
    syncAccounts: '/api/sync/accounts',
    syncLocations: '/api/sync/locations',
    syncReviews: '/api/sync/reviews',
    syncPosts: '/api/sync/posts',
    syncQuestions: '/api/sync/questions',
    
    // OAuth endpoints
    oauthInitiate: '/api/auth/initiate',
    oauthCallback: '/api/auth/callback',
    oauthStatus: '/api/auth/status',
  },
  
  // Request timeout in milliseconds
  timeout: isDevelopment ? 30000 : 15000,
  
  // Retry configuration
  retry: {
    maxAttempts: 3,
    initialDelay: 1000,
    maxDelay: 5000,
    backoffMultiplier: 2,
  }
}

/**
 * Sync Configuration
 */
export const SYNC_CONFIG = {
  // Sync intervals in milliseconds
  intervals: {
    reviews: isDevelopment ? 60 * 60 * 1000 : 30 * 60 * 1000, // 1 hour dev, 30 min prod
    posts: isDevelopment ? 2 * 60 * 60 * 1000 : 60 * 60 * 1000, // 2 hours dev, 1 hour prod
    questions: isDevelopment ? 60 * 60 * 1000 : 30 * 60 * 1000, // 1 hour dev, 30 min prod
    locations: isDevelopment ? 24 * 60 * 60 * 1000 : 12 * 60 * 60 * 1000, // 24 hours dev, 12 hours prod
    accounts: isDevelopment ? 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000, // 24 hours both
  },
  
  // Batch sizes for sync operations
  batchSizes: {
    reviews: 50,
    posts: 20,
    questions: 50,
    locations: 10,
  },
  
  // Rate limiting
  rateLimits: {
    requestsPerMinute: isDevelopment ? 30 : 60,
    requestsPerHour: isDevelopment ? 500 : 1000,
  },
  
  // Sync behavior
  behavior: {
    syncOnStartup: isDevelopment ? false : true,
    syncInBackground: true,
    retryFailedSyncs: true,
    maxConcurrentSyncs: isDevelopment ? 1 : 3,
  }
}

/**
 * API Rate Limiting Configuration
 */
export const RATE_LIMITS = {
  // GMB API limits (per project)
  gmb: {
    readRequestsPerMinute: 1000,
    writeRequestsPerMinute: 60,
    dailyQuota: 10000,
  },
  
  // Internal rate limiting
  internal: {
    requestsPerUserPerMinute: 60,
    requestsPerUserPerHour: 500,
    burstSize: 10,
  }
}

/**
 * Cache Configuration
 */
export const CACHE_CONFIG = {
  // TTL in milliseconds
  ttl: {
    accounts: 24 * 60 * 60 * 1000, // 24 hours
    locations: 12 * 60 * 60 * 1000, // 12 hours
    reviews: 30 * 60 * 1000, // 30 minutes
    posts: 60 * 60 * 1000, // 1 hour
    questions: 30 * 60 * 1000, // 30 minutes
    metrics: 15 * 60 * 1000, // 15 minutes
  },
  
  // Cache key prefixes
  keyPrefixes: {
    accounts: 'gmb:accounts:',
    locations: 'gmb:locations:',
    reviews: 'gmb:reviews:',
    posts: 'gmb:posts:',
    questions: 'gmb:questions:',
    metrics: 'gmb:metrics:',
  }
}

/**
 * Post Configuration
 */
export const POST_CONFIG = {
  // Maximum lengths
  maxLengths: {
    summary: 1500,
    callToActionUrl: 1200,
    eventTitle: 80,
    offerCouponCode: 50,
    offerTerms: 1500,
  },
  
  // Media constraints
  media: {
    maxPhotos: 10,
    maxVideoSize: 100 * 1024 * 1024, // 100MB
    maxPhotoSize: 5 * 1024 * 1024, // 5MB
    supportedImageFormats: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportedVideoFormats: ['mp4', 'mov', 'avi'],
  },
  
  // Event constraints
  events: {
    maxDurationDays: 365,
    minAdvanceHours: 1,
  },
  
  // Offer constraints
  offers: {
    maxDurationDays: 365,
    requiresCouponCode: false,
  }
}

/**
 * Review Management Configuration
 */
export const REVIEW_CONFIG = {
  // Auto-response settings
  autoResponse: {
    enabled: false, // Disabled by default, enable per franchise
    delayMinutes: isDevelopment ? 5 : 60, // Wait before auto-responding
    templates: {
      positive: "Thank you for your {rating}-star review! We're thrilled to hear about your positive experience with FixMyBrick.",
      neutral: "Thank you for taking the time to share your feedback. We appreciate your {rating}-star review and are always looking to improve.",
      negative: "Thank you for your feedback. We're sorry to hear about your experience and would like to make things right. Please contact us at your convenience.",
    },
    ratingThresholds: {
      positive: 4, // 4-5 stars
      neutral: 3, // 3 stars
      negative: 2, // 1-2 stars
    }
  },
  
  // Notification settings
  notifications: {
    newReviewAlert: true,
    negativeReviewAlert: true,
    unansweredThresholdHours: 24,
  },
  
  // Display settings
  display: {
    itemsPerPage: 20,
    showAnonymous: true,
    highlightUnanswered: true,
  }
}

/**
 * Question Management Configuration
 */
export const QUESTION_CONFIG = {
  // Auto-answer settings (for common questions)
  autoAnswer: {
    enabled: false,
    commonQuestions: {
      hours: "Our hours vary by location. Please visit our website or call for specific location hours.",
      services: "We offer comprehensive masonry services including brick repair, tuckpointing, chimney repair, and more. Visit fixmybrick.ca for details.",
      pricing: "Pricing varies by project scope. We offer free estimates - please contact us for a personalized quote.",
    }
  },
  
  // Notification settings
  notifications: {
    newQuestionAlert: true,
    unansweredThresholdHours: 48,
  },
  
  // Display settings
  display: {
    itemsPerPage: 20,
    highlightUnanswered: true,
  }
}

/**
 * Error Handling Configuration
 */
export const ERROR_CONFIG = {
  // Retry configuration for different error types
  retryStrategies: {
    network: {
      maxAttempts: 3,
      delay: 1000,
      backoff: 2,
    },
    rateLimit: {
      maxAttempts: 1,
      delay: 60000, // Wait 1 minute
      backoff: 1,
    },
    server: {
      maxAttempts: 2,
      delay: 5000,
      backoff: 2,
    }
  },
  
  // Error reporting
  reporting: {
    logToConsole: isDevelopment,
    logToSentry: isProduction,
    includeStackTrace: isDevelopment,
  }
}

/**
 * Feature Flags
 */
export const FEATURE_FLAGS = {
  // GMB Features
  gmb: {
    enabled: true,
    autoSync: isProduction,
    bulkOperations: true,
    mediaUpload: true,
    scheduledPosts: true,
    analyticsIntegration: false, // Coming soon
  },
  
  // UI Features
  ui: {
    darkMode: true,
    advancedFilters: true,
    bulkActions: true,
    exportData: true,
    realTimeUpdates: false, // Coming soon
    createPostEnhancedUI: isDevelopment,
    multiProfilePosting: isDevelopment,
  }
}

/**
 * Helper function to get sync interval for a specific type
 */
export function getSyncInterval(syncType: keyof typeof SYNC_CONFIG.intervals): number {
  return SYNC_CONFIG.intervals[syncType]
}

/**
 * Helper function to check if a feature is enabled
 */
export function isFeatureEnabled(feature: string): boolean {
  const [category, featureName] = feature.split('.')
  if (!category || !featureName) return false

  const categoryFlags = FEATURE_FLAGS[category as keyof typeof FEATURE_FLAGS]
  if (!categoryFlags) return false

  return (categoryFlags as Record<string, boolean>)[featureName] ?? false
}

/**
 * Helper function to get API endpoint URL
 */
export function getApiEndpoint(endpoint: string, ...params: string[]): string {
  const baseUrl = GMB_WORKER_CONFIG.baseUrl
  let path = GMB_WORKER_CONFIG.endpoints[endpoint as keyof typeof GMB_WORKER_CONFIG.endpoints]
  
  // If it's a function, call it with params
  if (typeof path === 'function') {
    path = (path as (...args: string[]) => string)(...params)
  }
  
  return `${baseUrl}${path}`
}
